import { motion } from "framer-motion";
import { countryFlag } from "../lib/derive";

/**
 * CareerPathBoard — chronological list of a mystery player's stints. Renders the
 * Career-Path round body on /lobby/:code/game. Reuses the design tokens.
 */
export function CareerPathBoard({ career = [], testId = "career-path-board" }) {
  if (!career.length) {
    return (
      <div className="text-zinc-500 text-sm italic" data-testid={`${testId}-empty`}>
        Loading career…
      </div>
    );
  }
  return (
    <section data-testid={testId} className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent)] font-bold">
          trace the career
        </span>
        <span className="text-[10px] text-zinc-600 uppercase tracking-widest">
          · {career.length} stints
        </span>
      </div>
      <ol
        className="relative grid gap-3 grid-cols-1 md:grid-cols-2"
        data-testid={`${testId}-list`}
      >
        {/* connecting line for single-column layout (mobile) */}
        <span
          aria-hidden
          className="md:hidden absolute left-[22px] top-2 bottom-2 w-px bg-white/10"
        />
        {career.map((s, i) => (
          <CareerStint key={`${s.team_id}-${s.start_year}-${i}`} stint={s} index={i} />
        ))}
      </ol>
    </section>
  );
}

function CareerStint({ stint, index }) {
  const isClub = stint.team_type === "club";
  const flag = countryFlag(stint.country);
  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.08 * index, duration: 0.35, ease: "easeOut" }}
      data-testid={`career-stint-${index}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-white/10 bg-[var(--surface)] hover:border-[var(--accent)]/30 transition"
    >
      <div
        className="shrink-0 w-11 h-11 rounded-full grid place-items-center overflow-hidden border border-white/10 bg-black"
        style={{ background: stint.badge_color || "#1a1a1a" }}
      >
        {stint.team_logo_url ? (
          <img
            src={stint.team_logo_url}
            alt=""
            className="w-9 h-9 object-contain"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span className="text-[10px] font-black text-white">
            {stint.team_name?.slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-[var(--text)] truncate">
          {stint.team_name}
        </div>
        <div className="text-[10px] uppercase tracking-widest mt-0.5 flex items-center gap-1.5 text-zinc-500">
          <span
            className={
              isClub
                ? "px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-300"
                : "px-1.5 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/30"
            }
          >
            {isClub ? "club" : "national team"}
          </span>
          {stint.country && (
            <span className="truncate">
              {flag} {stint.country}
            </span>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-[var(--accent)] font-black tabular-nums text-sm">
          {stint.start_year === stint.end_year
            ? stint.start_year
            : `${stint.start_year}–${stint.end_year}`}
        </div>
      </div>
    </motion.li>
  );
}

export default CareerPathBoard;
